'use client'
// NOTE: Dialog is being used from Ascend though the other icons and Dialog related components are
// coming directly from MUI. TODO - ask Ascend team to see if this can all come from them
import { Button } from '@achieve/ascend'
import { Typography } from 'components/Typography'
import { Dialog, DialogContent } from 'components/Dialog'
import { IconButton } from 'components/IconButton'
import PlayCircleOutlineIcon from '@mui/icons-material/PlayCircleOutline'
import CloseIcon from '@mui/icons-material/Close'

import { useState, useReducer, useEffect } from 'react'
import { VideoPlayer } from 'components/VideoPlayer'
import useViewportSize from 'hooks/useViewportSize'
import { videoMap } from 'constants/videoMappings'
import { useInlineMemoizedContentGetter } from 'utils/contentful'
import { useViewportSmallerThan } from 'utils/viewport'
import { BREAKPOINTS } from 'constants/viewport'

import { useTriggerEvents } from 'hooks/useTriggerEvents'
import { PlayButtonBackground } from 'components/PlayButtonBackground'

const reducerFunction = (state, action) => {
  switch (action.type) {
    case 'calculatedClient':
      return { ...state, ...action.payload }
    default:
      return state
  }
}

function VideoDialog({ content, trackOpen = {}, backgroundCta = false, autoOpen = false }) {
  const {
    explicitTitle,
    title,
    videoPlayer,
    altText,
    source,
    buttonCta = true,
  } = useInlineMemoizedContentGetter(content, [
    'explicitTitle',
    'title',
    'videoPlayer',
    'altText',
    'source',
    'buttonCta',
  ])
  const isMobile = useViewportSmallerThan(BREAKPOINTS.lg)
  const [open, setOpen] = useState(false)

  const { breakpointLetterSize, calculatedClient } = useViewportSize()
  const [state, dispatch] = useReducer(reducerFunction, {
    width: videoMap[breakpointLetterSize].width,
  })

  // Retrieving AnalyticsContext for dispatching events
  const { fireAndForget } = useTriggerEvents()

  useEffect(() => {
    if (calculatedClient) {
      dispatch({
        type: 'calculatedClient',
        payload: {
          width: videoMap[breakpointLetterSize].width,
        },
      })
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [calculatedClient, breakpointLetterSize])

  // Auto-open dialog when autoOpen is true
  useEffect(() => {
    if (autoOpen) {
      setOpen(true)
    }
  }, [autoOpen])

  const handleClickOpen = () => {
    fireAndForget({
      ...trackOpen,
      list_name: 'Video Dialog CTA Button Click',
      click_id: 'Watch the story',
      click_type: 'Button Click',
      click_text: title,
      click_url: source,
      track_event: 'button_click',
      event_action: 'button_click',
      event_type: 'button_click',
    })
    setOpen(true)
  }

  const handleClose = () => {
    setOpen(false)
  }

  return (
    <>
      {backgroundCta ? (
        <PlayButtonBackground onClick={handleClickOpen} />
      ) : (
        <Button
          data-testid="video-dialog-button"
          data-video-dialog-open={true}
          color="primary"
          variant={buttonCta || buttonCta === null ? 'contained' : 'text'}
          onClick={handleClickOpen}
          className={`[&_svg]:text-neutral-900 **:data-[testid="circle-icon"]:ml-2 **:[[class*="endIcon"]]:ml-2 ${
            !buttonCta ? 'pl-0' : ''
          }`}
          endIcon={<PlayCircleOutlineIcon data-testid="circle-icon" />}
        >
          {!explicitTitle && (
            <Typography
              className={` ${!buttonCta ? 'pl-6' : ''}`}
              variant="bodyLg"
              fontWeight="bold"
            >
              {title}
            </Typography>
          )}
          {explicitTitle && explicitTitle}
        </Button>
      )}
      <Dialog
        data-testid="video-dialog"
        className={'bg-neutral-900 md:bg-black/65'}
        fullWidth={false}
        maxWidth={false}
        fullScreen={isMobile}
        open={open}
        onClose={handleClose}
        PaperProps={{
          sx: {
            width: isMobile ? 'max-content' : state.width,
            height: 'max-content',
            backgroundColor: 'transparent',
          },
        }}
      >
        <DialogContent data-testid="dialog-content" className={'overflow-hidden m-0 p-0'}>
          <div className="pt-8 lg:pt-10 bg-[#1d252f] relative">
            <IconButton
              data-testid="icon-button"
              className="bg-[white] shadow-none absolute z-1 right-4 top-2 p-2 opacity-75 w-4 h-4 lg:w-6 lg:h-6"
              onClick={handleClose}
              data-video-dialog-close={true}
            >
              <CloseIcon data-video-dialog-close={true} className="w-4 h-4 lg:w-6 lg:h-6" />
            </IconButton>
            <VideoPlayer
              trackOnPlay={{
                nav_link_section: 'Testimonials',
              }}
              content={
                !source
                  ? { ...videoPlayer, autoplay: true, openFullScreen: true }
                  : { altText, source, autoplay: true, openFullScreen: true }
              }
              maxWidth="true"
            />
          </div>
        </DialogContent>
      </Dialog>
    </>
  )
}

export { VideoDialog }
